import moment, { Moment } from "moment";
import { HttpClient, HttpErrorResponse } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable, catchError, delay, map, shareReplay, take, tap, throwError } from "rxjs";
import { isString } from "lodash";

import {
	Enviroment,
	LogService,
	PagedQueryParameter,
	PagedResponse,
	PaginationModel,
	UpdateDateHttpInterceptor
} from "@nstep-common/core";

import {
	expandedLog,
	toastHttpError,
	toastGenericError,
	Cache,
	JsonMapper,
	toastRequestDenied,
	Any,
	removePrototype,
	getCaller
} from "@nstep-common/utils";

@Injectable({ providedIn: 'root' })
export class ApiService {
	protected apiUrl: string;
	private cacheMap: { [key: string]: Cache<any> } = {};

	constructor(
		private httpClient: HttpClient,
		private environment: Enviroment,
		private logService: LogService
	) {
		const hostUrl = `${window.location.protocol}//${window.location.host}`;

		this.apiUrl = this.environment.apiUrl.startsWith('http')
			? this.environment.apiUrl
			: `${hostUrl}${this.environment.apiUrl}`;
	}

	private request<T>(
		method: (url: string, body?: any, options?: any) => Observable<any>,
		type: { new(): T } | [{ new(): T }],
		relativeUrl: string,
		body?: any,
		options?: any,
		useDefaultErrorHandler = true,
		cacheable = false
	): Observable<T> | Observable<T[]> {
		const fullUrl = `${this.apiUrl}/${relativeUrl}`;
		const observable = this.createHttpObservable<T>(method, type, fullUrl, body, options, useDefaultErrorHandler);
		return cacheable ? this.createCachedObservable<T>(relativeUrl, options, observable) : observable;
	}

	private createHttpObservable<T>(
		method: (url: string, body?: any, options?: any) => Observable<any>,
		type: { new(): T } | [{ new(): T }],
		url: string,
		body: any | null,
		options: any,
		useDefaultErrorHandler = true
	): Observable<T> | Observable<T[]> {
		const startedAt = moment.utc();
		const caller = getCaller(5);
		options = { withCredentials: true, ...(options ?? {}) };

		let observable = method.call(this.httpClient, url, body ?? options, options)
			.pipe(
				take(1),
				tap(res => this.logRequest(url, body ?? options, res, true, startedAt, caller))
			);

		if (useDefaultErrorHandler) {
			observable = observable.pipe(
				catchError(err => this.handleError(url, body ?? options, err, caller))
			);
		}

		return Array.isArray(type)
			? observable.pipe(map(res => this.mapArrayResponse(type, res)))
			: observable.pipe(map(res => this.mapObjectResponse(type, res)));
	}

	private mapObjectResponse<T>(type: { new(): T }, result: any): T {
		const jsonMapper = new JsonMapper();
		const deserialized = jsonMapper.deserializeObject(result, type);

		if (deserialized.hasErrors()) {
			this.logService.error("Bad item format received from server.", deserialized.errors);
		}

		return deserialized.value;
	}

	private mapArrayResponse<T>(type: [{ new(): T }], result: any): T[] {
		const jsonMapper = new JsonMapper();
		const deserialized = jsonMapper.deserializeArray(result, type[0]);

		if (deserialized.hasErrors()) {
			this.logService.error("Bad array format received from server.", deserialized.errors);
		}

		return deserialized.values;
	}

	private handleError(url: string, params: any, resp: any, caller?: string): Observable<never> {
		this.logRequest(url, params, resp, false, undefined, caller);

		let errors = [];
		let isError = false;

		if (resp instanceof HttpErrorResponse) {
			if (resp.status === 400) {
				if (resp.error.errors) {
					errors = resp.error.errors;
				}
				else {
					const message = isString(resp.error)
						? resp.error
						: resp.error.detail && isString(resp.error.detail)
							? resp.error.detail
							: JSON.stringify(resp.error);

					toastRequestDenied(message);
				}
			}
			else if (resp.status === 0) {
				toastRequestDenied('The server is currently offline, please try again later.');
			}
			else {
				toastHttpError(resp);
				isError = true;
			}
		} else if (resp.error?.detail) {
			toastRequestDenied(resp.error.detail);
		} else {
			toastGenericError();
			isError = true;
		}

		if (isError) {
			this.logService.publisher('api').error('Request error: ' + url, {
				request: this.stringifyForLog(params),
				resp
			});
		}
		else {
			this.logService.publisher('api').warn('Request warning: ' + url, {
				request: this.stringifyForLog(params),
				resp
			});
		}

		return throwError(() => errors);
	}

	private logRequest(target: string, params: any, response: any, isSuccess: boolean, startedAt?: Moment, caller?: string) {
		if (!this.environment.logRequests) return;

		const duration = startedAt ? moment.duration(moment.utc().diff(startedAt)).asSeconds() : null;

		console.groupCollapsed(`REQUEST ${duration ? `(${duration}s)` : ''}%c ${isSuccess ? 'SUCCESS' : 'FAIL'}%c ${target} %c${caller ? `\nfrom ${caller}` : ''}`,
			`color: ${isSuccess ? 'limegreen' : 'red'}`,
			'font-weight: normal; color: orange',
			'font-weight: normal; color: grey');

		const formattedParams = { ...params };
		UpdateDateHttpInterceptor.formatDates(formattedParams);

		if (this.environment.expandedLogRequests) {
			expandedLog(formattedParams, 'PARAMS');
			expandedLog(response, 'RESPONSE');
		} else {
			console.info('PARAMS', removePrototype(formattedParams));
			console.info('RESPONSE', removePrototype(response));
		}

		console.groupEnd();
	}

	private stringifyForLog(value: any): string {
		try {
			return JSON.stringify(value) ?? '[No serializable log data]';
		}
		catch {
			return '[Unserializable log data]';
		}
	}

	private createCachedObservable<T>(relativeUrl: string, options: any, observable: Observable<T> | Observable<T[]>): Observable<T> | Observable<T[]> {
		const cache = this.cacheMap[relativeUrl] = this.cacheMap[relativeUrl] ?? new Cache<T>();

		let cachedObservable = cache.getValue(options);

		if (!cachedObservable) {
			cachedObservable = (observable as Observable<any>).pipe(delay(50), shareReplay(1));
			cache.setValue(cachedObservable, options);
		}

		return cachedObservable;
	}

	get<T>(type: { new(): T }, relativeUrl: string, options?: any, useDefaultErrorHandler?: boolean): Observable<T>;
	get<T>(type: [{ new(): T }], relativeUrl: string, options?: any, useDefaultErrorHandler?: boolean): Observable<T[]>;
	get<T>(type: { new(): T } | [{ new(): T }], relativeUrl: string, options?: any, useDefaultErrorHandler = true): Observable<T> | Observable<T[]> {
		return this.request(this.httpClient.get<T>, type, relativeUrl, null, options, useDefaultErrorHandler, true);
	}

	post<T>(type: { new(): T }, relativeUrl: string, body?: any, options?: any, useDefaultErrorHandler?: boolean): Observable<T>;
	post<T>(type: [{ new(): T }], relativeUrl: string, body?: any, options?: any, useDefaultErrorHandler?: boolean): Observable<T[]>;
	post<T>(type: { new(): T } | [{ new(): T }], relativeUrl: string, body?: any, options?: any, useDefaultErrorHandler = true): Observable<T> | Observable<T[]> {
		return this.request(this.httpClient.post<T>, type, relativeUrl, body, options, useDefaultErrorHandler);
	}

	put<T>(type: { new(): T }, relativeUrl: string, body?: any, options?: any, useDefaultErrorHandler?: boolean): Observable<T>;
	put<T>(type: [{ new(): T }], relativeUrl: string, body?: any, options?: any, useDefaultErrorHandler?: boolean): Observable<T[]>;
	put<T>(type: { new(): T } | [{ new(): T }], relativeUrl: string, body?: any, options?: any, useDefaultErrorHandler = true): Observable<T> | Observable<T[]> {
		return this.request(this.httpClient.put<T>, type, relativeUrl, body, options, useDefaultErrorHandler);
	}

	patch<T>(type: { new(): T }, relativeUrl: string, body?: any, options?: any, useDefaultErrorHandler?: boolean): Observable<T>;
	patch<T>(type: [{ new(): T }], relativeUrl: string, body?: any, options?: any, useDefaultErrorHandler?: boolean): Observable<T[]>;
	patch<T>(type: { new(): T } | [{ new(): T }], relativeUrl: string, body?: any, options?: any, useDefaultErrorHandler = true): Observable<T> | Observable<T[]> {
		return this.request(this.httpClient.patch<T>, type, relativeUrl, body, options, useDefaultErrorHandler);
	}

	delete<T>(type: { new(): T }, relativeUrl: string, options?: any, useDefaultErrorHandler?: boolean): Observable<T>;
	delete<T>(type: [{ new(): T }], relativeUrl: string, options?: any, useDefaultErrorHandler?: boolean): Observable<T[]>;
	delete<T>(type: { new(): T } | [{ new(): T }], relativeUrl: string, options?: any, useDefaultErrorHandler = true): Observable<T> | Observable<T[]> {
		return this.request(this.httpClient.delete<T>, type, relativeUrl, null, options, useDefaultErrorHandler);
	}

	invalidateEntireCache(): void {
		for (let cacheKey of Object.keys(this.cacheMap)) {
			this.invalidateCache(cacheKey);
		}
	}

	invalidateCache(relativeUrl: string): void {
		this.cacheMap[relativeUrl]?.clear();
	}

	invalidatePartialMatchCache(partialUrl: string): void {
		Object.keys(this.cacheMap)
			.filter(k => k.startsWith(partialUrl))
			.forEach(key => this.cacheMap[key].clear());
	}

	getPage<T>(type: [{ new(): T }], relativeUrl: string, model: PagedQueryParameter, useDefaultErrorHandler = true): Observable<PagedResponse<T>> {
		return this.get(Any, relativeUrl, { observe: 'response', params: model }, useDefaultErrorHandler)
			.pipe(map((res: any) => {
				const page = this.mapObjectResponse(PaginationModel, JSON.parse(res.headers.get('X-Pagination')));
				const results = this.mapArrayResponse(type, res.body);

				return new PagedResponse<T>({ page, results });
			}));
	}

	postPage<T>(type: [{ new(): T }], relativeUrl: string, model: PagedQueryParameter, useDefaultErrorHandler = true): Observable<PagedResponse<T>> {
		return this.post(Any, relativeUrl, model, { observe: 'response' }, useDefaultErrorHandler)
			.pipe(map((res: any) => {
				const page = this.mapObjectResponse(PaginationModel, JSON.parse(res.headers.get('X-Pagination')));
				const results = this.mapArrayResponse(type, res.body);

				return new PagedResponse<T>({ page, results });
			}));
	}

	postNoContent(relativeUrl: string, body?: any, options?: any, useDefaultErrorHandler = true): Observable<any> {
		return this.request(this.httpClient.post<any>, Any, relativeUrl, body, options, useDefaultErrorHandler);
	}

	putNoContent(relativeUrl: string, body?: any, options?: any, useDefaultErrorHandler = true): Observable<any> {
		return this.request(this.httpClient.put<any>, Any, relativeUrl, body, options, useDefaultErrorHandler);
	}

	patchNoContent(relativeUrl: string, body?: any, options?: any, useDefaultErrorHandler = true): Observable<any> {
		return this.request(this.httpClient.patch<any>, Any, relativeUrl, body, options, useDefaultErrorHandler);
	}

	deleteNoContent(relativeUrl: string, options?: any, useDefaultErrorHandler = true): Observable<any> {
		return this.request(this.httpClient.delete<any>, Any, relativeUrl, null, options, useDefaultErrorHandler);
	}
}
